import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { Banner, BannerDescription } from '@wartek-id/banner'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Link } from '@wartek-id/link'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIGetListRealisasiV2,
  useAPIPelaksanaanProgress,
} from 'api/kinerja/useAPIKinerja'
import PanelSummary from 'app/KinerjaPage/components/PanelSummary'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { formatDate } from 'utils/datetime'
import DialogConfirmEdit from './components/DialogConfirmEdit'
import { getBadgeByStateV2, getCtaByState } from './utils'

export const MIN_POINT = 32

const LoadingState = () => {
  return (
    <div className="flex justify-center p-6" data-testid="loading-spinner">
      <Spinner />
    </div>
  )
}

const PengembanganKompetensiApp = () => {
  const { push } = useRouter()
  const { data, isLoading } = useAPIGetListRealisasiV2('PK')
  const { data: progressData } = useAPIPelaksanaanProgress()
  const period = progressData?.data?.period ?? 1

  const isEvaluated = progressData?.data?.evaluationDocumentState === 'READY'

  const dataKinerjaOrdered = data?.data ?? []
  const kinerjaUpdatedDate = data?.kinerjaUpdated?.find(
    (e) => e.kategori === 'PK'
  )

  const totalPoint = data?.bobotTotal ?? 0

  let bgPointColor = 'bg-[#ECFEF2]'
  let pointStatus = 'Sudah memenuhi ekspektasi'

  if (totalPoint > MIN_POINT) {
    bgPointColor = 'bg-[#E7F6FE]'
    pointStatus = 'Melebihi ekspektasi'
  } else if (totalPoint < MIN_POINT) {
    bgPointColor = 'bg-[#FFEECE]'
    pointStatus = 'Belum memenuhi ekspektasi'
  }

  const guideLink =
    'https://docs.google.com/document/d/1G4WV_rXbJh4CoKg45c7eKeyyHY8wR8pVwwdVRjz9j90'

  const [showConfirmEditRHK, setShowConfirmEditRHK] = useState(false)

  return (
    <div className="py-[50px] px-[116px]">
      <div className="flex justify-between items-center mb-11">
        <div className="flex items-center gap-3">
          {/*
           * Note: we need to explicitly set font-size and line-height as workaround of styles collision
           * due to multiple version of @wartek-id/icon being used in this project.
           */}
          <Icon
            data-testid="icon-back"
            className="text-black !text-4xl !leading-9"
            fontSize="lg"
            onClick={() => push(PENGELOLAAN_KINERJA_GURU_PATH)}
          >
            arrow_back
          </Icon>
          <Text variant="display-md">Pengembangan Kompetensi</Text>
        </div>

        <PanelSummary
          items={[
            {
              label: 'Semester',
              value: period,
            },
          ]}
        />
      </div>

      <Banner
        className="mb-9"
        animationDuration={300}
        title="Poin dan kelengkapan dokumen tidak berpengaruh langsung ke Predikat Kinerja"
        type="informational"
        closable
      >
        <BannerDescription>
          Jika total poin belum memenuhi ekspektasi, Anda tidak perlu khawatir
          dan dapat tetap fokus mengembangkan kompetensi sesuai rencana. Yang
          terpenting adalah upaya dan progres Anda dalam mengembangkan diri,
          bukan angka dan poin yang diperoleh.
        </BannerDescription>
      </Banner>

      <div className="bg-white rounded-lg p-6">
        <div className="flex">
          <div className="flex flex-col gap-1 flex-1">
            <Text variant="heading-md">Progres pengumpulan dokumen</Text>
            <Text variant="body" color="subdued">
              Kumpulkan berdasarkan Perencanaan Kinerja yang Anda dan atasan
              sudah sepakati.
            </Text>
          </div>

          <Button
            color="white"
            disabled={isEvaluated}
            onClick={() => {
              if (!isEvaluated) {
                setShowConfirmEditRHK(true)
              }
            }}
          >
            Ubah Rencana Hasil Kerja
          </Button>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : (
          <>
            <TablePelaksanaanKinerja data-testid="table-kompetensi">
              <TableHeader>
                <TableHeaderItem className="w-5">No.</TableHeaderItem>
                <TableHeaderItem className="pl-14 w-[500px]">
                  Rencana Hasil Kerja
                </TableHeaderItem>
                <TableHeaderItem>Poin</TableHeaderItem>
                <TableHeaderItem>Status</TableHeaderItem>
                <TableHeaderItem />
              </TableHeader>

              <TableBody>
                {dataKinerjaOrdered.map((kinerja, index) => (
                  <TableRow key={`row-kinerja-${kinerja.id}`}>
                    <TableRowItem className="text-center">
                      {index + 1}
                    </TableRowItem>
                    <TableRowItem className="pl-14">
                      {kinerja.deskripsi}
                    </TableRowItem>
                    <TableRowItem>{kinerja.bobot ?? 0} poin</TableRowItem>
                    <TableRowItem>
                      {getBadgeByStateV2(kinerja.state)}
                    </TableRowItem>
                    <TableRowItem>
                      <NextLink
                        href={`${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/pengembangan-kompetensi/${
                          kinerja.id
                        }/bukti-dukung?sequence=${index + 1}`}
                        passHref
                      >
                        {getCtaByState(kinerja.state)}
                      </NextLink>
                    </TableRowItem>
                  </TableRow>
                ))}
                <TableRow className={bgPointColor}>
                  <TableRowItem />
                  <TableRowItem className="pl-14">
                    <div className="flex flex-col gap-1">
                      <Text variant="body-sm-bold">TOTAL POIN</Text>
                      <Text color="subdued" variant="body-sm">
                        Cek selengkapnya pada
                        <Link href={guideLink} target="_blank" rel="noopener">
                          peraturan yang berlaku.
                        </Link>
                      </Text>
                    </div>
                  </TableRowItem>
                  <TableRowItem colSpan={3}>
                    <Text data-testid="total-point" variant="body-sm-bold">
                      {totalPoint} poin ({pointStatus})
                    </Text>
                  </TableRowItem>
                </TableRow>
              </TableBody>
            </TablePelaksanaanKinerja>
          </>
        )}
      </div>
      {kinerjaUpdatedDate && (
        <div className="mt-5 bg-white px-3 py-1 flex justify-center rounded-md">
          <Text variant="body" color="subdued">
            Rencana Hasil Kerja diubah pada:{' '}
            {formatDate(kinerjaUpdatedDate.kinerjaUpdatedAt, 'd MMMM yyyy')}
          </Text>
        </div>
      )}

      <DialogConfirmEdit
        data-testid="dialog-confirm-edit-rhk"
        customSize={595}
        isOpen={showConfirmEditRHK}
        onClose={() => setShowConfirmEditRHK(false)}
        onConfirm={() => {
          push(
            `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/pengembangan-kompetensi/edit`
          )
        }}
      />
    </div>
  )
}

export default PengembanganKompetensiApp
